import { useMergedRef } from '@mantine/hooks';
import clsx from 'clsx';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { getQTipData } from '../Tooltip/qTip.utilities';
import { disabledNativeFieldClassNames } from '../utils/fieldStyleClasses';
import { setValidInputDimension } from '../utils/validateStyleDimension';
import type { InputLengthStyleProps, TextFieldProps } from './TextField.types';

const errorClassNames = 'tw:border-sq-danger';

const borderColorClassNames = `
  tw:border-sq-disabled-gray
  tw:hover:border-sq-theme-dark
  tw:focus:border-sq-theme-dark
  tw:active:border-sq-theme-dark
  tw:dark:border-sq-dark-disabled-gray
  tw:dark:hover:border-sq-theme-darker
  tw:dark:focus:border-sq-theme-darker
  tw:dark:active:border-sq-theme-darker
`;

const baseClassNames = `
  tw:h-8.5
  tw:border
  tw:border-solid
  tw:p-1
  tw:px-3
  tw:py-1
  tw:leading-normal
  tw:placeholder-gray-400!
  tw:outline-none
  tw:dark:placeholder-sq-dark-text/30!
`;

const textFieldStyle = 'specTextField';

const darkThemeClassNames = `
  tw:dark:bg-sq-dark-background
  tw:dark:text-sq-dark-text
`;
const lightThemeClassNames = `
  tw:text-sq-text-color
`;

/**
 * Textfield.
 */
export const TextField = forwardRef<HTMLInputElement, TextFieldProps & InputLengthStyleProps>((props, ref) => {
  const {
    readonly = false,
    disabled = false,
    onChange,
    onPaste,
    onKeyUp,
    onFocus,
    onBlur,
    onKeyDown,
    id,
    name,
    size = 'sm',
    value,
    placeholder,
    extraClassNames = '',
    testId,
    type = 'text',
    inputGroup,
    step,
    showError,
    errorText,
    min,
    max,
    maxLength,
    minLength,
    required = false,
    autoComplete = 'off',
    inputWidth,
    inputHeight,
    autoFocus = false,
    ...tooltipProps
  } = props;

  const internalRef = useRef<HTMLInputElement | null>(null);
  const mergedRef = useMergedRef(ref, internalRef);
  const [cursor, setCursor] = useState<number | null>(null);
  const tooltipData = getQTipData(tooltipProps);
  const initialAutoFocusRef = useRef(autoFocus);

  useEffect(() => {
    if (initialAutoFocusRef.current) {
      setTimeout(() => {
        internalRef.current?.focus();
      }, 0);
    }
  }, []);

  useEffect(() => {
    const input = internalRef.current;
    if (input && type !== 'number' && type !== 'email') input.setSelectionRange(cursor, cursor);
  }, [ref, cursor, value, type]);

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setCursor(e.target.selectionStart);
    onChange && onChange(e);
  };

  const shouldResetNumberStepBase = () => {
    if (type !== 'number') return false;
    if (min === undefined) return true;

    return min % Number(step ?? 1) === 0;
  };

  const stringifyInputValue = (inputValue: string | string[] | number) =>
    Array.isArray(inputValue) ? inputValue.join(',') : String(inputValue);

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (shouldResetNumberStepBase()) {
      const input = e.currentTarget;
      const blurValue = input.value;

      input.defaultValue = '';
      input.value =
        input.validity.valid && value !== null && value !== undefined ? stringifyInputValue(value) : blurValue;
    }
    onBlur?.(e);
  };

  // Number inputs are uncontrolled (see valueProp below) — sync external value changes
  // to the DOM here. Skip while focused so we don't clobber the user mid-type.
  useEffect(() => {
    const input = internalRef.current;

    if (type !== 'number') return;
    if (value !== null && value !== undefined && value !== input?.value && input) {
      input.value = stringifyInputValue(value);
    }
  }, [value, type]);

  let borderRadiusClassNames = 'tw:rounded-md';
  if (inputGroup === 'left') {
    borderRadiusClassNames = 'tw:rounded-l-md tw:border-r-0 tw:focus:border-r tw:active:border-r';
  } else if (inputGroup === 'right') {
    borderRadiusClassNames = 'tw:rounded-r-md tw:border-l-0 tw:focus:border-l tw:active:border-l';
  }

  const appliedClassNames = clsx(
    baseClassNames,
    disabledNativeFieldClassNames,
    extraClassNames,
    lightThemeClassNames,
    darkThemeClassNames,
    borderRadiusClassNames,
    showError ? errorClassNames : borderColorClassNames,
    {
      'tw:text-sm': size === 'sm',
      'tw:text-xl': size === 'lg',
    },
    textFieldStyle,
  );

  const inputProp = setValidInputDimension(inputWidth, inputHeight)
    ? {
        style: setValidInputDimension(inputWidth, inputHeight),
      }
    : {};
  const inputLenghtProp: InputLengthStyleProps = {};
  if (maxLength) inputLenghtProp.maxLength = maxLength;
  if (minLength) inputLenghtProp.minLength = minLength;

  // CRAB-59299: number inputs use `defaultValue` (uncontrolled) so React doesn't keep
  // rewriting the value attribute — that attribute is the browser's step base, and
  // controlled writes caused wrong arrow increments / step-mismatch errors. `handleBlur`
  // clears defaultValue so the step base falls back to `min`/0.
  const valueProp = type === 'number' ? { defaultValue: value } : { value };

  return (
    <>
      <input
        ref={mergedRef}
        data-testid={testId}
        name={name}
        id={id}
        type={type}
        {...valueProp}
        className={appliedClassNames}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readonly}
        autoComplete={autoComplete}
        onChange={handleChange}
        onPaste={onPaste}
        onFocus={onFocus}
        onBlur={handleBlur}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        step={step}
        required={required}
        min={min}
        max={max}
        {...inputLenghtProp}
        {...inputProp}
        {...tooltipData}
      />
      {errorText && showError && <div className="tw:mt-1 tw:text-xs tw:text-sq-danger">{errorText}</div>}
    </>
  );
});
TextField.displayName = 'TextField';
